// WACategoryPricesPanel — v1 (2026-09-30)
// أسعار Meta لكل فئة قالب (تسويق/خدمة/مصادقة) — تُستخدم لحساب التكلفة
// التقديرية لحملات الشرائح. تقرأ/تكتب /api/whatsapp/category-prices القائم
// (لا نقطة API جديدة). الصلاحية نفسها المستخدَمة في PUT ذلك المسار (admin/ceo).
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { formatKSADateTime } from "../lib/ksaTime";

const fmt = (iso) => {
  try { return formatKSADateTime(new Date(iso), { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || ""; }
};

const FIELDS = [
  { key: "marketing", label: "تسويق (MARKETING)" },
  { key: "utility", label: "خدمة (UTILITY)" },
  { key: "authentication", label: "مصادقة (AUTHENTICATION)" },
];

export default function WACategoryPricesPanel() {
  const [values, setValues] = useState({ marketing: "", utility: "", authentication: "" });
  const [currency, setCurrency] = useState("SAR");
  const [meta, setMeta] = useState({ set_by: null, set_at: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/category-prices");
      setValues({
        marketing: data.marketing ?? "", utility: data.utility ?? "",
        authentication: data.authentication ?? "",
      });
      setCurrency(data.currency || "SAR");
      setMeta({ set_by: data.set_by, set_at: data.set_at });
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تحميل أسعار الفئات"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/whatsapp/category-prices", {
        marketing: values.marketing === "" ? null : values.marketing,
        utility: values.utility === "" ? null : values.utility,
        authentication: values.authentication === "" ? null : values.authentication,
        currency,
      });
      setMeta({ set_by: data.set_by, set_at: data.set_at });
      toast.success("✅ حُفظت أسعار رسائل واتساب");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="wa-category-prices-panel" dir="rtl" style={{ padding: 18, maxWidth: 620, marginTop: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: "var(--brown-deep)", marginBottom: 4 }}>
        💰 أسعار رسائل واتساب
      </div>
      <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.9, marginBottom: 16 }}>
        سعر Meta لكل رسالة حسب فئة القالب — يُستخدم لحساب التكلفة التقديرية للحملات.
      </div>

      {loading ? (
        <div style={{ color: "var(--muted)", fontSize: 13 }}>جارٍ التحميل…</div>
      ) : (
        <>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: "block", fontSize: 12, color: "var(--muted)", fontWeight: 700, marginBottom: 6 }}>
              العملة
            </label>
            <input data-testid="wa-price-currency" type="text" value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={{ width: 100, minHeight: 40, borderRadius: 10, padding: "6px 10px", fontSize: 13,
                       textAlign: "center", border: "1px solid rgba(140,90,30,0.35)", background: "#fff" }} />
          </div>

          {FIELDS.map((f) => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, color: "var(--muted)", fontWeight: 700, marginBottom: 6 }}>
                {f.label}
              </label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  data-testid={`wa-price-input-${f.key}`}
                  type="number" step="0.01" min={0}
                  value={values[f.key]}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder="فارغ = بلا سعر"
                  style={{
                    width: 160, minHeight: 44, borderRadius: 10, padding: "8px 12px",
                    fontSize: 15, fontWeight: 700, textAlign: "center",
                    border: "1px solid rgba(140,90,30,0.35)", background: "#fff", color: "var(--brown-deep)",
                  }}
                />
                <span style={{ fontSize: 12, color: "var(--muted)" }}>{currency || "—"} / رسالة</span>
              </div>
            </div>
          ))}

          <button
            data-testid="wa-category-prices-save"
            className="btn btn-primary"
            disabled={saving}
            onClick={save}
            style={{ minHeight: 44, padding: "8px 22px", fontWeight: 800 }}
          >
            {saving ? "⏳ جارٍ الحفظ…" : "حفظ"}
          </button>

          {meta.set_by && (
            <div data-testid="wa-category-prices-meta" style={{ fontSize: 12, color: "var(--muted)", marginTop: 14 }}>
              آخر تعديل بواسطة <b>{meta.set_by}</b> · {fmt(meta.set_at)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
